import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const ease = [0.16, 1, 0.3, 1];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INVESTMENT = ["Under $250k", "$250k – $500k", "$500k – $1M", "$1M+"];
const EXPERIENCE = ["None", "Hospitality / F&B", "Retail", "Investor / Operator", "Other"];

const initial = {
    name: "",
    email: "",
    phone: "",
    city: "",
    investment_range: "",
    experience: "",
    message: "",
};

export default function FranchiseBand() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.city) {
            toast.error("Please share your name, email and target city.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/franchise`, form);
            setDone(true);
            setForm(initial);
            toast.success("Franchise inquiry received. We'll be in touch.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Unable to submit. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const closeAndReset = () => {
        setOpen(false);
        setTimeout(() => setDone(false), 300);
    };

    return (
        <section
            id="franchise"
            data-testid="franchise-band"
            className="relative bg-obsidian-900 border-y border-white/10 py-16 md:py-20 overflow-hidden"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, ease }}
                    className="grid grid-cols-12 gap-6 items-center"
                >
                    <div className="col-span-12 md:col-span-8">
                        <div className="flex items-center gap-4 mb-4">
                            <span className="w-10 h-px bg-amber-glow" />
                            <span className="overline">For Operators</span>
                        </div>
                        <h2 className="font-serif-display text-3xl md:text-5xl leading-[1] tracking-tighter">
                            Bring Chill <span className="italic text-[#E63946]">Blox</span> to your city.
                        </h2>
                        <p className="mt-4 text-ice-200/70 max-w-xl">
                            We partner with a small number of operators each year. If you run a serious bar,
                            restaurant group, or hospitality venture — we&rsquo;d like to hear from you.
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-4 flex md:justify-end">
                        <Dialog.Root open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTimeout(() => setDone(false), 300); }}>
                            <Dialog.Trigger asChild>
                                <button
                                    data-testid="franchise-open-btn"
                                    className="lux-btn lux-btn-primary"
                                >
                                    Franchise Inquiry
                                    <span aria-hidden>→</span>
                                </button>
                            </Dialog.Trigger>
                            <Dialog.Portal>
                                <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
                                <Dialog.Content
                                    data-testid="franchise-modal"
                                    className="fixed left-1/2 top-1/2 z-[70] w-[92vw] max-w-[640px] -translate-x-1/2 -translate-y-1/2 border border-white/10 bg-obsidian-800 p-8 md:p-10 max-h-[90vh] overflow-y-auto"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <span className="overline">Franchise</span>
                                            <Dialog.Title className="mt-3 font-serif-display text-3xl md:text-4xl tracking-tight">
                                                A serious <span className="italic text-amber-glow">introduction</span>.
                                            </Dialog.Title>
                                        </div>
                                        <Dialog.Close asChild>
                                            <button
                                                aria-label="Close"
                                                data-testid="franchise-close-btn"
                                                className="text-ice-400 hover:text-amber-glow text-2xl leading-none transition-colors"
                                            >
                                                ×
                                            </button>
                                        </Dialog.Close>
                                    </div>
                                    <Dialog.Description className="text-ice-200/70 text-sm mt-4 mb-8">
                                        Share a few details. Our partnerships lead will reply personally within 48 hours.
                                    </Dialog.Description>

                                    {done ? (
                                        <div data-testid="franchise-success" className="py-8">
                                            <span className="font-script text-amber-glow text-3xl">merci.</span>
                                            <h3 className="font-serif-display text-2xl md:text-3xl mt-3">
                                                Your inquiry is with us.
                                            </h3>
                                            <p className="text-ice-200/70 mt-4 text-sm leading-relaxed">
                                                A member of our partnerships team will be in touch within 48 hours.
                                            </p>
                                            <button
                                                onClick={closeAndReset}
                                                data-testid="franchise-done-close"
                                                className="lux-btn lux-btn-ghost mt-8"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    ) : (
                                        <form onSubmit={onSubmit} data-testid="franchise-form" className="space-y-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <Field label="Full Name" required>
                                                    <input
                                                        data-testid="fr-name"
                                                        className="lux-input"
                                                        value={form.name}
                                                        onChange={update("name")}
                                                        required
                                                    />
                                                </Field>
                                                <Field label="Email" required>
                                                    <input
                                                        data-testid="fr-email"
                                                        type="email"
                                                        className="lux-input"
                                                        value={form.email}
                                                        onChange={update("email")}
                                                        required
                                                    />
                                                </Field>
                                                <Field label="Phone">
                                                    <input
                                                        data-testid="fr-phone"
                                                        className="lux-input"
                                                        value={form.phone}
                                                        onChange={update("phone")}
                                                        placeholder="Optional"
                                                    />
                                                </Field>
                                                <Field label="Target City" required>
                                                    <input
                                                        data-testid="fr-city"
                                                        className="lux-input"
                                                        value={form.city}
                                                        onChange={update("city")}
                                                        placeholder="e.g. Mumbai"
                                                        required
                                                    />
                                                </Field>
                                                <Field label="Investment Range">
                                                    <select
                                                        data-testid="fr-investment"
                                                        className="lux-input appearance-none"
                                                        value={form.investment_range}
                                                        onChange={update("investment_range")}
                                                    >
                                                        <option value="" className="bg-obsidian-800">Select</option>
                                                        {INVESTMENT.map((o) => (
                                                            <option key={o} value={o} className="bg-obsidian-800">{o}</option>
                                                        ))}
                                                    </select>
                                                </Field>
                                                <Field label="Experience">
                                                    <select
                                                        data-testid="fr-experience"
                                                        className="lux-input appearance-none"
                                                        value={form.experience}
                                                        onChange={update("experience")}
                                                    >
                                                        <option value="" className="bg-obsidian-800">Select</option>
                                                        {EXPERIENCE.map((o) => (
                                                            <option key={o} value={o} className="bg-obsidian-800">{o}</option>
                                                        ))}
                                                    </select>
                                                </Field>
                                            </div>

                                            <Field label="Tell us about your venture">
                                                <textarea
                                                    data-testid="fr-message"
                                                    className="lux-input resize-none"
                                                    rows={3}
                                                    value={form.message}
                                                    onChange={update("message")}
                                                    placeholder="Concept, locations, timeline…"
                                                />
                                            </Field>

                                            <div className="flex items-center justify-between pt-4">
                                                <span className="text-[0.65rem] uppercase tracking-[0.3em] text-ice-400">
                                                    Confidential · Reply within 48h
                                                </span>
                                                <button
                                                    type="submit"
                                                    disabled={submitting}
                                                    data-testid="fr-submit"
                                                    className="lux-btn lux-btn-primary"
                                                >
                                                    {submitting ? "Sending…" : "Submit Inquiry"}
                                                    {!submitting && <span aria-hidden>→</span>}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </Dialog.Content>
                            </Dialog.Portal>
                        </Dialog.Root>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-[0.65rem] uppercase tracking-[0.3em] text-ice-400 mb-1">
                {label}
                {required ? <span className="text-amber-glow"> *</span> : null}
            </span>
            {children}
        </label>
    );
}
